// Sort elements into visual cascade order for navigation stagger
// animations. The same problem as in the gridder editor's Build
// Animation: when elements in the same visual "row" have slightly
// different rendered `top` pixels (because of `align="top"` vs
// `align="middle"` vs `align="bottom"`), a pure Euclidean
// `sqrt(left² + top²)` sort scatters them and the resulting cascade
// looks zigzag, not rhythmic.
//
// Fix: prefer the grid model (row → col) when the element is — or
// lives inside — a `.col`. The `.col` itself carries `colstart-N` /
// `colend-N` classes, and its row ancestor carries `row-N`. That model
// matches what the designer set up in the editor, so the cascade
// animates as clean L→R waves down the page. Auto Layout rows are the
// exception: they render with flex wrapping, so the grid classes no
// longer describe the visual order. Those fall back to rect sorting.
//
// For elements outside the grid (header / logo / menu — passed via
// `uiFadeTargetsForStaggerSync` in the navigation controller), fall
// back to a rect-based key: bin `top` into bands so slight pixel
// differences inside the same visible row collapse together, then sort
// by `left` within the band. Same row-band intent, applied to free
// elements.

const ROW_BAND_HEIGHT_PX = 60
const DETAIL_RECT_SORT_SELECTOR = '.thumbnail-wrap, .lay-thumbnailgrid-load-more-wrap'

function parseCssIndex(value) {
	const n = Number.parseInt(value, 10)
	return Number.isNaN(n) ? 0 : n
}

function getClosestCol(el) {
	if (el.classList && el.classList.contains('col')) {
		return el
	}
	if (typeof el.closest === 'function') {
		return el.closest('.col')
	}
	return null
}

function readColIndex(col) {
	let colIdx = 0
	const cls = (col.className || '').toString().split(/\s+/)
	for (const c of cls) {
		if (c.indexOf('colstart-') === 0) colIdx = parseCssIndex(c.slice('colstart-'.length))
	}
	return colIdx
}

function readRowIndex(col) {
	let row = 0
	let n = col.parentElement
	while (n) {
		const ncls = (n.className || '').toString().split(/\s+/)
		for (const c of ncls) {
			if (c.indexOf('row-') === 0 && c.length > 'row-'.length) {
				const r = parseCssIndex(c.slice('row-'.length))
				if (r || c === 'row-0') {
					row = r
					n = null
					break
				}
			}
		}
		if (n) n = n.parentElement
	}
	return row
}

function isInsideAutoLayoutRow(col) {
	if (!col || typeof col.closest !== 'function') {
		return false
	}
	if (col.closest('.row.lay-auto-layout-row')) {
		return true
	}
	const columnWrap = col.closest('.column-wrap')
	if (!columnWrap || columnWrap.classList.contains('_100vh') || columnWrap.classList.contains('rowcustomheight')) {
		return false
	}
	try {
		const style = window.getComputedStyle(columnWrap)
		return style.display.indexOf('flex') !== -1 && style.flexWrap !== 'nowrap'
	} catch (e) {
		return false
	}
}

function getInnerStackPosition(col, stackCol) {
	const stackElement = col.closest('.stack-element')
	if (!stackElement) {
		const stackRect = stackCol.getBoundingClientRect()
		const colRect = col.getBoundingClientRect()
		return Math.floor((colRect.top - stackRect.top) / ROW_BAND_HEIGHT_PX)
	}
	const stackElements = Array.prototype.slice.call(stackCol.querySelectorAll('.stack-element'))
	const index = stackElements.indexOf(stackElement)
	return index === -1 ? 0 : index
}

function readColPos(el) {
	// Direct `.col` or walk up to find one.
	const col = getClosestCol(el)
	if (!col) return null
	if (isInsideAutoLayoutRow(col)) return null

	const stackCol = typeof col.closest === 'function' ? col.closest('.col.type-stack') : null
	const isInnerStackCol = stackCol && stackCol !== col
	if (isInnerStackCol) {
		return {
			row: readRowIndex(stackCol),
			col: readColIndex(stackCol),
			stackRow: getInnerStackPosition(col, stackCol),
			innerCol: readColIndex(col),
		}
	}

	return {
		row: readRowIndex(col),
		col: readColIndex(col),
		stackRow: -1,
		innerCol: 0,
	}
}

function readBandedRect(el) {
	const r = el.getBoundingClientRect()
	const scrollX = window.scrollX || window.pageXOffset || 0
	const scrollY = window.scrollY || window.pageYOffset || 0
	const top = r.top + scrollY
	const left = r.left + scrollX
	return {
		band: Math.floor(top / ROW_BAND_HEIGHT_PX),
		left: left,
	}
}

function isDetailRectSortTarget(el) {
	return !!(el && el.matches && el.matches(DETAIL_RECT_SORT_SELECTOR))
}

// mutates `elements` in place, no return needed.
// `transition_type` reserved for future direction-aware variants (e.g.
// reverse the cascade for `stagger-up`); currently same origin for all.
var sortByVisual = function (elements, transition_type) {
	const keyByEl = new Map()
	for (var i = 0; i < elements.length; i++) {
		var el = elements[i]
		var grid = readColPos(el)
		if (grid && isDetailRectSortTarget(el)) {
			// Thumbnailgrid detail targets live inside one `.col`, but their
			// DOM order does not match their visual flow once Load More is
			// involved. Keep the parent grid row/col ordering, and only sort
			// the children inside that slot by their rendered position.
			var detailRect = readBandedRect(el)
			keyByEl.set(el, { primary: grid.row, secondary: grid.col, stackRow: detailRect.band, innerCol: detailRect.left, hasGrid: true })
			continue
		}
		if (grid) {
			// grid model: row -> col. Inner stack columns keep the
			// outer stack's row/col position, then cascade top-to-bottom
			// within the stack before sorting by inner column.
			keyByEl.set(el, { primary: grid.row, secondary: grid.col, stackRow: grid.stackRow, innerCol: grid.innerCol, hasGrid: true })
		} else {
			// fallback: banded rect — same row band collapses to one
			// "primary" value, `left` is the tiebreaker.
			var rect = readBandedRect(el)
			keyByEl.set(el, { primary: rect.band, secondary: rect.left, stackRow: 0, innerCol: 0, hasGrid: false })
		}
	}

	elements.sort(function (a, b) {
		const ka = keyByEl.get(a)
		const kb = keyByEl.get(b)
		// `.col` elements sort relative to each other; non-`.col` UI
		// elements sort relative to each other. Avoid mixing the two
		// (different value scales — row indices are 0..N, bands are
		// 0..thousands depending on viewport) by checking hasGrid first
		// and putting grid elements after UI in the order if mixed
		// (rare — callers usually pass homogeneous arrays).
		if (ka.hasGrid !== kb.hasGrid) return ka.hasGrid ? 1 : -1
		return (ka.primary - kb.primary) || (ka.secondary - kb.secondary) || (ka.stackRow - kb.stackRow) || (ka.innerCol - kb.innerCol)
	})
}

export { sortByVisual }
